import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Bot, Edit, Trash2, Plus } from "lucide-react";

type AiService = {
  id: string;
  name: string;
  provider: string;
  model_code: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export default function ConfiguracoesIAs() {
  const { toast } = useToast();
  const [aiServices, setAiServices] = useState<AiService[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<AiService | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    provider: "google",
    model_code: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    loadAiServices();
  }, []);

  const loadAiServices = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ai_services")
      .select("*")
      .order("provider, name");

    if (error) {
      toast({
        title: "Erro ao carregar serviços",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setAiServices(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const serviceData = {
      name: formData.name,
      provider: formData.provider,
      model_code: formData.model_code,
      description: formData.description || null,
      is_active: formData.is_active,
    };

    if (editingService) {
      const { error } = await supabase
        .from("ai_services")
        .update(serviceData)
        .eq("id", editingService.id);

      if (error) {
        toast({
          title: "Erro ao atualizar serviço",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Serviço atualizado",
          description: "O serviço de IA foi atualizado com sucesso.",
        });
        loadAiServices();
        handleDialogClose();
      }
    } else {
      const { error } = await supabase
        .from("ai_services")
        .insert([serviceData]);

      if (error) {
        toast({
          title: "Erro ao criar serviço",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Serviço criado",
          description: "O serviço de IA foi criado com sucesso.",
        });
        loadAiServices();
        handleDialogClose();
      }
    }
  };

  const handleEdit = (service: AiService) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      provider: service.provider,
      model_code: service.model_code,
      description: service.description || "",
      is_active: service.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este serviço de IA?")) return;

    const { error } = await supabase
      .from("ai_services")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: "Erro ao excluir serviço",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Serviço excluído",
        description: "O serviço de IA foi excluído com sucesso.",
      });
      loadAiServices();
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      provider: "google",
      model_code: "",
      description: "",
      is_active: true,
    });
    setEditingService(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações de IA</h1>
          <p className="text-muted-foreground">Gerencie os serviços de inteligência artificial</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Serviço
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingService ? "Editar" : "Adicionar"} Serviço de IA
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Google Gemini 2.5 Flash"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="provider">Provedor</Label>
                  <Select
                    value={formData.provider}
                    onValueChange={(value) => setFormData({ ...formData, provider: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model_code">Código do Modelo</Label>
                  <Input
                    id="model_code"
                    value={formData.model_code}
                    onChange={(e) => setFormData({ ...formData, model_code: e.target.value })}
                    placeholder="Ex: gemini-2.5-flash"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do serviço e suas capacidades..."
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Serviço ativo</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingService ? "Atualizar" : "Criar"} Serviço
                </Button>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {aiServices.map((service) => (
          <Card key={service.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">{service.name}</CardTitle>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(service.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {service.provider.charAt(0).toUpperCase() + service.provider.slice(1)} • {service.model_code}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {service.description && (
                <div>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </div>
              )}
              <div>
                <Badge variant={service.is_active ? "default" : "secondary"}>
                  {service.is_active ? "✓ Ativo" : "Inativo"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {aiServices.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhum serviço de IA cadastrado. Clique em "Adicionar Serviço" para começar.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
